import { useState, useCallback, useRef, useEffect } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { Play, RotateCcw, Trash2, CheckCircle, Terminal, Code2, AlertCircle, Info, Clock, FolderOpen, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import InputModal from '../components/InputModal';
import { hasTurtleImport, stripTurtleImport, injectTurtleModule, extractTurtleSvg, resetTurtleState } from '../services/turtleModule';
import { getVirtualFileSystemCode } from '../services/virtualFileSystem';
import { translateErrorToChinese } from '../utils/errorLineAdjust';

const defaultCode = `# 欢迎使用 Python 在线编程环境
# 在这里编写你的 Python 代码，点击"运行"即可执行

print("Hello, Python!")

# 示例：计算斐波那契数列
def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

print("斐波那契数列前10项:")
for i in range(10):
    print(f"F({i}) = {fibonacci(i)}")
`;

const inputExampleCode = `# input() 交互示例
name = input("请输入你的名字：")
age = input("请输入你的年龄：")
print(f"你好，{name}！你今年 {age} 岁。")
`;

const fileExampleCode = `# 文件操作示例
# 写入文件
with open("test.txt", "w") as f:
    f.write("Hello, File!\\n")
    f.write("这是第二行\\n")

# 读取文件
with open("test.txt", "r") as f:
    content = f.read()
    print("文件内容：")
    print(content)

# 逐行读取
with open("test.txt", "r") as f:
    print("逐行读取：")
    for line in f:
        print(f"  - {line.strip()}")
`;

const turtleExampleCode = `# Turtle 海龟画图示例
import turtle

# 设置画布
turtle.setup(600, 400)
turtle.bgcolor("lightblue")
turtle.title("海龟画图演示")

# 设置画笔
turtle.pensize(3)
turtle.pencolor("red")
turtle.speed(5)

# 画一个正方形
for i in range(4):
    turtle.forward(100)
    turtle.right(90)

# 移动到新位置画圆
turtle.penup()
turtle.goto(150, 0)
turtle.pendown()
turtle.pencolor("green")
turtle.circle(50)

# 画一个填充三角形
turtle.penup()
turtle.goto(-150, 0)
turtle.pendown()
turtle.pencolor("blue")
turtle.fillcolor("yellow")
turtle.begin_fill()
for i in range(3):
    turtle.forward(80)
    turtle.left(120)
turtle.end_fill()

# 写文字
turtle.penup()
turtle.goto(0, -80)
turtle.pencolor("purple")
turtle.write("海龟画图测试成功!", align="center", font=("Arial", 16, "bold"))

turtle.hideturtle()
print("海龟画图完成！")
`;

export default function PythonPlayground() {
  const [code, setCode] = useState(defaultCode);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState(0);
  const [copied, setCopied] = useState(false);
  const [pyodideReady, setPyodideReady] = useState(false);
  const [turtleSvg, setTurtleSvg] = useState<string | null>(null);
  const [inputData, setInputData] = useState('');
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputPrompt, setInputPrompt] = useState('');
  const inputResolverRef = useRef<((value: string) => void) | null>(null);
  const [virtualFiles, setVirtualFiles] = useState<string[]>([]);
  const pyodideRef = useRef<any>(null);

  useEffect(() => {
    const preloadPyodide = async () => {
      try {
        setIsLoading(true);
        setError('');

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js';
        script.async = true;

        await new Promise<void>((resolve, reject) => {
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Pyodide script'));
          document.head.appendChild(script);
        });

        const loadPyodide = (window as any).loadPyodide;
        if (!loadPyodide) {
          throw new Error('loadPyodide not found on window');
        }

        pyodideRef.current = await loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/',
        });

        setPyodideReady(true);
      } catch (err: any) {
        setError('Python 环境加载失败: ' + (err.message || '请检查网络连接后刷新页面重试'));
      } finally {
        setIsLoading(false);
      }
    };

    preloadPyodide();
  }, []);

  const setupInputHandler = useCallback((pyodide: any) => {
    const inputLines = inputData.split('\n').filter(line => line.trim() !== '');
    let inputIndex = 0;

    const inputHandler = (prompt: string = ''): string => {
      if (inputIndex < inputLines.length) {
        const value = inputLines[inputIndex];
        inputIndex++;
        return value;
      }
      return '';
    };

    pyodide.globals.set('_js_input', inputHandler);

    pyodide.runPython(`
import sys
import types

def _custom_input(prompt=''):
    result = _js_input(prompt)
    if prompt:
        print(prompt + result)
    else:
        print(result)
    return result

sys.modules['builtins'].input = _custom_input
    `);
  }, [inputData]);

  const setupVirtualFileSystem = useCallback((pyodide: any) => {
    pyodide.runPython(getVirtualFileSystemCode());
  }, []);

  const getVirtualFilesList = useCallback((pyodide: any): string[] => {
    try {
      const files = pyodide.runPython(`list(_virtual_files.keys())`);
      return files ? Array.from(files) : [];
    } catch {
      return [];
    }
  }, []);

  const handleInputSubmit = useCallback((value: string) => {
    setInputModalOpen(false);
    if (inputResolverRef.current) {
      inputResolverRef.current(value);
      inputResolverRef.current = null;
    }
  }, []);

  const handleInputCancel = useCallback(() => {
    setInputModalOpen(false);
    if (inputResolverRef.current) {
      inputResolverRef.current('');
      inputResolverRef.current = null;
    }
  }, []);

  const handleRun = useCallback(async () => {
    if (!pyodideRef.current) {
      setError('Python 环境尚未加载完成，请稍后再试');
      return;
    }

    setIsRunning(true);
    setOutput('');
    setError('');
    setTurtleSvg(null);
    setExecutionTime(0);

    const startTime = performance.now();

    try {
      const pyodide = pyodideRef.current;
      let stdout = '';

      pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
      pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

      setupVirtualFileSystem(pyodide);

      setupInputHandler(pyodide);

      if (hasTurtleImport(code)) {
        await injectTurtleModule(pyodide);
        await resetTurtleState(pyodide);
        await pyodide.runPythonAsync(stripTurtleImport(code));
      } else {
        await pyodide.runPythonAsync(code);
      }

      const elapsed = Math.round(performance.now() - startTime);
      setExecutionTime(elapsed);
      setOutput(stdout || '(程序执行完成，无输出)');

      if (hasTurtleImport(code)) {
        const svgResult = await extractTurtleSvg(pyodide);
        if (svgResult) {
          setTurtleSvg(svgResult);
        }
      }

      const files = getVirtualFilesList(pyodide);
      setVirtualFiles(files);
    } catch (err: any) {
      const translatedError = translateErrorToChinese(err.message || '执行出错', 0);
      setError(translatedError);
    } finally {
      setIsRunning(false);
    }
  }, [code, setupInputHandler, setupVirtualFileSystem, getVirtualFilesList]);

  const handleReset = useCallback(() => {
    setCode(defaultCode);
    setOutput('');
    setError('');
    setTurtleSvg(null);
    setExecutionTime(0);
    setInputData('');
    setVirtualFiles([]);
  }, []);

  const handleClear = useCallback(() => {
    setCode('');
    setOutput('');
    setError('');
    setTurtleSvg(null);
    setExecutionTime(0);
    setInputData('');
    setVirtualFiles([]);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      
    }
  }, [code]);

  const loadExample = useCallback((type: 'default' | 'input' | 'file' | 'turtle') => {
    switch (type) {
      case 'input':
        setCode(inputExampleCode);
        break;
      case 'file':
        setCode(fileExampleCode);
        break;
      case 'turtle':
        setCode(turtleExampleCode);
        break;
      default:
        setCode(defaultCode);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-4 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">Py</span>
                </div>
                <div>
                  <h3 className="font-bold text-white">代码编辑器</h3>
                  <p className="text-xs text-gray-400">Python 3.10+ 语法支持 | 支持 input() 交互和文件操作</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  onChange={(e) => loadExample(e.target.value as any)}
                  className="px-3 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  defaultValue="default"
                >
                  <option value="default">基础示例</option>
                  <option value="input">input() 示例</option>
                  <option value="file">文件操作示例</option>
                  <option value="turtle">海龟画图示例</option>
                </select>
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  清空
                </button>
                <button
                  onClick={handleReset}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <RotateCcw className="w-4 h-4" />
                  重置
                </button>
                <button
                  onClick={handleRun}
                  disabled={isRunning || isLoading || !pyodideReady}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
                >
                  <Play className="w-4 h-4" />
                  {isRunning ? '运行中...' : '运行代码'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-4">
              <div className="col-span-3 border-r border-gray-200">
                <CodeMirror
                  value={code}
                  height="500px"
                  theme="dark"
                  extensions={[python()]}
                  onChange={(value) => setCode(value)}
                  basicSetup={{
                    lineNumbers: true,
                    highlightActiveLineGutter: true,
                    highlightActiveLine: true,
                    foldGutter: true,
                  }}
                />
              </div>

              <div className="col-span-1 bg-gray-900 h-[500px] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-gray-700 bg-gray-800">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-white">输出结果</span>
                  </div>
                  {executionTime > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>{executionTime}ms</span>
                    </div>
                  )}
                </div>

                <div className="p-3 flex-1 overflow-auto">
                  <AnimatePresence mode="wait">
                    {isRunning ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center h-full"
                      >
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          <p className="text-gray-400 text-xs">执行中...</p>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {error ? (
                          <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 text-red-400 mb-1.5">
                              <AlertCircle className="w-3.5 h-3.5" />
                              <span className="font-medium text-xs">错误</span>
                            </div>
                            <pre className="text-xs text-red-300 whitespace-pre-wrap font-mono">
                              {error}
                            </pre>
                          </div>
                        ) : output ? (
                          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                            <div className="flex items-center gap-1.5 text-green-400 mb-1.5">
                              <CheckCircle className="w-3.5 h-3.5" />
                              <span className="font-medium text-xs">成功</span>
                            </div>
                            <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                              {output}
                            </pre>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            <div className="text-center">
                              <Terminal className="w-8 h-8 mx-auto mb-2 opacity-30" />
                              <p className="text-xs">点击运行代码</p>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {virtualFiles.length > 0 && (
                  <div className="border-t border-gray-700 p-3 bg-gray-800/50">
                    <div className="flex items-center gap-2 mb-2">
                      <FolderOpen className="w-4 h-4 text-yellow-400" />
                      <span className="text-xs font-medium text-gray-300">虚拟文件系统</span>
                    </div>
                    <div className="space-y-1 max-h-24 overflow-auto">
                      {virtualFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-gray-400">
                          <FileText className="w-3 h-3" />
                          <span className="font-mono">{file}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-medium text-gray-600">预填充输入数据（可选）：</label>
                <span className="text-xs text-gray-400">每行一个输入值，用于 input() 函数</span>
              </div>
              <textarea
                value={inputData}
                onChange={(e) => setInputData(e.target.value)}
                placeholder="例如：&#10;张三&#10;18&#10;（每行对应一次 input() 调用）"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none font-mono"
                rows={3}
              />
            </div>
          </div>

          <AnimatePresence>
            {turtleSvg && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="mt-4"
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 border-b border-emerald-200 flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 2 L12 9 M12 15 L12 22"/>
                    </svg>
                    <span className="font-medium text-emerald-700">海龟画图</span>
                  </div>
                  <div
                    className="p-3 overflow-auto"
                    style={{ maxHeight: '500px' }}
                    dangerouslySetInnerHTML={{ __html: turtleSvg }}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Code2 className="w-4 h-4 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">Python 版本</h3>
              </div>
              <p className="text-xs text-gray-600">支持 Python 3.10+ 语法特性，使用 Pyodide 在浏览器本地运行</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Terminal className="w-4 h-4 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">input() 支持</h3>
              </div>
              <p className="text-xs text-gray-600">支持交互式输入，可通过预填充或弹窗方式提供输入数据</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-4 h-4 text-yellow-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">文件操作</h3>
              </div>
              <p className="text-xs text-gray-600">支持虚拟文件系统，可进行文件读写操作（open、read、write）</p>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Info className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm">使用提示</h3>
              </div>
              <p className="text-xs text-gray-600">首次加载较慢，请等待环境初始化完成后再运行代码</p>
            </div>
          </div>
        </div>
      </div>

      <InputModal
        isOpen={inputModalOpen}
        prompt={inputPrompt}
        onSubmit={handleInputSubmit}
        onCancel={handleInputCancel}
      />
    </div>
  );
}